import { ref, computed } from "vue";
import { useOrdersStore } from "~/projects/order-manager/stores/ordersStore";
import { DeliveryPartnerType } from "~/projects/order-manager/types/enums/DeliveryPartnerType";

/** The column on the user each partner's agent mapping is stored in. */
export type PartnerAgentFieldName =
  | "karbari_agent_id"
  | "nuport_agent_id"
  | "bizmotion_agent_id";

/**
 * One selectable agent, flattened from a partner integration's metadata.
 */
export interface PartnerAgentOption {
  /** What gets stored on the user's `*_agent_id` column. */
  value: string | number;
  label: string;
  /** Secondary detail shown beside the label (internal id, mobile, status). */
  hint: string | null;
  /** Which integration this agent came from. */
  integrationId: number;
  integrationName: string;
}

/**
 * One agent field to render — a partner type with at least one configured
 * integration, whether or not its agents have been synced yet.
 */
export interface PartnerAgentField {
  key: string;
  field: PartnerAgentFieldName;
  label: string;
  placeholder: string;
  agents: PartnerAgentOption[];
  /**
   * True when more than one integration of this partner contributed agents, so
   * the template can name the integration each agent belongs to.
   */
  showIntegrationName: boolean;
  /**
   * Set when the partner is configured but has no agents to offer. The field is
   * still rendered, disabled, with this as the reason — a configured partner
   * silently vanishing from the form gives no clue that a sync is what's
   * missing.
   */
  emptyReason: string | null;
}

interface PartnerAgentSource {
  key: string;
  /** Display name of the partner, e.g. "BizMotion". */
  name: string;
  type: DeliveryPartnerType;
  field: PartnerAgentFieldName;
  /** Where this partner keeps its people in `meta_data`. */
  metaKey: string;
  toOption: (agent: any) => {
    value: string | number;
    label: string;
    hint: string | null;
  };
}

/**
 * The partners that map a SmartComm user onto one of their own people. Each
 * writes to its own column, so a user can be mapped to several partners at once.
 */
const PARTNER_AGENT_SOURCES: PartnerAgentSource[] = [
  {
    key: "karbari",
    name: "Karbari",
    type: DeliveryPartnerType.Karbari,
    field: "karbari_agent_id",
    metaKey: "users",
    toOption: (agent) => ({
      value: agent.id,
      label: agent.full_name || agent.user_name,
      hint: agent.status === 0 ? "Inactive" : null,
    }),
  },
  {
    key: "nuport",
    name: "Nuport",
    type: DeliveryPartnerType.Nuport,
    field: "nuport_agent_id",
    metaKey: "users",
    toOption: (agent) => ({
      value: agent.id,
      label:
        agent.name ||
        agent.full_name ||
        agent.user_name ||
        agent.email ||
        agent.id,
      hint: agent.internalId || null,
    }),
  },
  {
    key: "bizmotion",
    name: "BizMotion",
    type: DeliveryPartnerType.BizMotion,
    field: "bizmotion_agent_id",
    metaKey: "agents",
    toOption: (agent) => ({
      value: agent.employee_id,
      label: agent.name || agent.email || agent.mobile || agent.employee_id,
      hint: agent.mobile || null,
    }),
  },
];

/**
 * Collects the partner-agent dropdowns a user form should show.
 *
 * Every configured partner that maps agents gets its own field, and a partner
 * with several integrations lists the agents from all of them. Previously only
 * the first integration was read, which hid the other integrations' agents —
 * and hid the partner entirely when that first one had no metadata yet.
 *
 * The integrations are fetched once and shared by all partners rather than
 * re-requested per partner.
 */
export function usePartnerAgents() {
  const ordersStore = useOrdersStore();

  const integrations = ref<any[]>([]);
  const loading = ref(false);

  const load = async () => {
    loading.value = true;
    try {
      const response =
        (await ordersStore.getDeliveryPartnerIntegrations()) as any;

      if (!response) {
        integrations.value = [];
        return;
      }

      integrations.value = Array.isArray(response)
        ? response
        : response.data || [response];
    } catch (error) {
      console.error("Error fetching delivery partner integrations:", error);
      integrations.value = [];
    } finally {
      loading.value = false;
    }
  };

  const partnerAgentFields = computed<PartnerAgentField[]>(() => {
    return PARTNER_AGENT_SOURCES.flatMap((source) => {
      const partnerIntegrations = integrations.value.filter(
        (i: any) => i.type === source.type,
      );

      // Nothing configured for this partner, so there is nothing to map onto.
      if (!partnerIntegrations.length) {
        return [];
      }

      const contributing = partnerIntegrations.filter(
        (i: any) => i?.meta_data?.[source.metaKey]?.length,
      );

      const agents = contributing.flatMap((integration: any) =>
        integration.meta_data[source.metaKey]
          .map((agent: any) => {
            const option = source.toOption(agent);

            // An agent with no usable id cannot be stored against the user.
            if (option.value === undefined || option.value === null) {
              return null;
            }

            return {
              ...option,
              integrationId: integration.id,
              integrationName:
                integration.name || `Integration ${integration.id}`,
            };
          })
          .filter(Boolean),
      ) as PartnerAgentOption[];

      // A sync writes the key even when the partner returned nobody, so its
      // presence separates "never synced" from "synced and the partner has no
      // staff" — two problems with two different fixes.
      const hasSynced = partnerIntegrations.some((i: any) =>
        Array.isArray(i?.meta_data?.[source.metaKey]),
      );

      let emptyReason: string | null = null;
      if (!agents.length) {
        emptyReason = hasSynced
          ? `No agents found in ${source.name}. Add staff there, then re-sync this integration.`
          : `Not synced yet — run "Sync ${source.name}" on the Delivery Partner Integrations page.`;
      }

      return [
        {
          key: source.key,
          field: source.field,
          label: `${source.name} Agent`,
          placeholder: agents.length
            ? `Select ${source.name} Agent`
            : `No ${source.name} agents available`,
          agents,
          showIntegrationName: contributing.length > 1,
          emptyReason,
        },
      ];
    });
  });

  return { load, loading, integrations, partnerAgentFields };
}
